import type {LoginForm} from "@/stores/user"
import {useUserStore} from "@/stores/user"
import type {AxiosResponse} from "@/plugins/axios"
import type {App} from "vue"
import {UserTypes} from "@/enums/UserTypes"

export interface Auth {
  user: App.Models.AuthUser | null
  login: (form: LoginForm) => Promise<AxiosResponse<{ access_token: string }>>
  loginUsingToken: (token: string, loadUser?: boolean) => Promise<void>
  loadUser: (force?: boolean) => Promise<void>
  logout: () => Promise<void>
  impersonate: (userId: number) => Promise<AxiosResponse<{ access_token: string }>>
  stopImpersonating: () => Promise<AxiosResponse<{ access_token: string }>>
  loggedIn: boolean
  subscription: any
  hasActiveSubscription: boolean
  isOnTrial: boolean
  isOnGracePeriod: boolean
  daysTillNextPayment: boolean
  isBeneficiary: boolean
  isAdmin: boolean
  isUser: boolean
  isOwnerDeceased: boolean
}

export const $auth: Auth = {
  get user(): App.Models.AuthUser | null {
    const userStore = useUserStore()
    return userStore.user
  },
  set user(user: App.Models.AuthUser | null) {
    const userStore = useUserStore()
    userStore.user = user
  },
  async login(form: LoginForm) {
    const userStore = useUserStore()

    return await userStore.login(form)
  },
  async loginUsingToken(token: string, loadUser: boolean = true) {
    const userStore = useUserStore()

    await userStore.loginUsingToken(token, loadUser)
  },
  async loadUser(force: boolean = false) {
    const userStore = useUserStore()
    return await userStore.loadUser(force)
  },
  logout() {
    const userStore = useUserStore()
    return userStore.logout()
  },
  get loggedIn() {
    return !!this.user
  },
  get subscription() {
    return this.user?.account?.current_subscription
  },
  get hasActiveSubscription() {
    return this.user?.account?.has_active_subscription
  },
  get isOnTrial() {
    return this.user?.account?.is_on_trial
  },
  get isOnGracePeriod() {
    return this.user?.account?.is_on_grace_period
  },
  get daysTillNextPayment() {
    return this.user?.account?.days_till_next_payment
  },
  async impersonate(userId: number) {
    const userStore = useUserStore()
    return await userStore.impersonate(userId)
  },
  async stopImpersonating() {
    const userStore = useUserStore()
    return await userStore.stopImpersonating()
  },
  get isBeneficiary(): boolean {
    return this.user?.user_type === UserTypes.BENEFICIARY
  },
  get isAdmin(): boolean {
    return this.user?.user_type === UserTypes.ADMIN
  },
  get isUser(): boolean {
    return this.user?.user_type === UserTypes.USER
  },
  get isOwnerDeceased(): boolean {
    return this.user?.user_type === UserTypes.BENEFICIARY && this.user?.owner_deceased
  }
}

export default {
  install(app: App) {
    app.config.globalProperties.$auth = $auth
  }
}
